import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  TextInput,
  Switch,
  Platform,
  Dimensions,
  ActivityIndicator,
  Linking,
} from 'react-native';
import { theme as baseTheme } from '../styles/theme';
import { useAuth } from '../hooks/useAuth';
import { useConnections } from '../hooks/useConnections';
import { useSharing } from '../hooks/useSharing';
import { getFunctions, httpsCallable } from 'firebase/functions';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

const getBreakpoint = (w) => {
  if (w < 480) return 'mobile';
  if (w < 768) return 'mobileLarge';
  if (w < 1024) return 'tablet';
  return 'desktop';
};

// SVG icons
const ArrowLeftIcon = ({ color = '#000', size = 20 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>←</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12,19 5,12 12,5"/>
    </svg>
  );
};

const UserIcon = ({ color = '#000', size = 20 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>👤</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );
};

const MusicIcon = ({ color = '#000', size = 20 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>♪</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
    </svg>
  );
};

const PaletteIcon = ({ color = '#000', size = 20 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>🎨</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r=".5" fill={color}/>
      <circle cx="17.5" cy="10.5" r=".5" fill={color}/>
      <circle cx="8.5" cy="7.5" r=".5" fill={color}/>
      <circle cx="6.5" cy="12.5" r=".5" fill={color}/>
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
    </svg>
  );
};

const ShieldIcon = ({ color = '#000', size = 20 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>🔒</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
};

const CheckIcon = ({ color = '#000', size = 16 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>✓</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12"/>
    </svg>
  );
};

const UsersIcon = ({ color = '#000', size = 20 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>👥</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
};

const CrownIcon = ({ color = '#000', size = 20 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>♛</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
      <path d="M2 19h20v2H2v-2zM2 17l4-9 6 4 4-6 4 6-4 5H2v0z"/>
    </svg>
  );
};

const ExternalLinkIcon = ({ color = '#000', size = 16 }) => {
  if (!isWeb) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  );
};

// Section card
const SettingsSection = ({ icon, title, children, theme }) => (
  <View style={[styles.section, { backgroundColor: theme.surface }]}>
    <View style={styles.sectionHeader}>
      <View style={[styles.sectionIconWrap, { backgroundColor: theme.primary + '18' }]}>
        {icon}
      </View>
      <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>{title}</Text>
    </View>
    <View style={styles.sectionBody}>{children}</View>
  </View>
);

// Setting row
const SettingRow = ({ label, description, right, theme, onPress }) => (
  <Pressable onPress={onPress} style={[styles.settingRow, { borderBottomColor: theme.borderLight || theme.border + '40' }]}>
    <View style={{ flex: 1 }}>
      <Text style={[styles.settingLabel, { color: theme.textPrimary }]}>{label}</Text>
      {description && <Text style={[styles.settingDesc, { color: theme.textMuted }]}>{description}</Text>}
    </View>
    {right}
  </Pressable>
);

const PAGE_TURN_MODES = [
  { id: 'timer', label: 'Auto (Timer)' },
  { id: 'manual', label: 'Manual' },
  { id: 'pedal', label: 'Pedal' },
];

export const SettingsScreen = ({ navigation }) => {
  const { user, signOut, updateUserProfile } = useAuth();
  const { connections, pendingConnections, sentRequests, respondToConnection, removeConnection, sendConnectionRequest, getConnectionStatus } = useConnections();
  const { searchUsers } = useSharing();
  const [screenWidth, setScreenWidth] = useState(SCREEN_WIDTH);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Connection search state
  const [connSearchEmail, setConnSearchEmail] = useState('');
  const [connSearchResult, setConnSearchResult] = useState(null);
  const [connSearching, setConnSearching] = useState(false);
  const [connSearchError, setConnSearchError] = useState('');
  const [connSent, setConnSent] = useState(false);

  // Profile
  const [displayName, setDisplayName] = useState(user?.displayName || '');

  // Viewer settings
  const [pageTurnMode, setPageTurnMode] = useState(user?.settings?.defaultPageTurnMode || 'manual');
  const [defaultBpm, setDefaultBpm] = useState(String(user?.settings?.defaultBPM || 120));

  // Appearance
  const [themePreference, setThemePreference] = useState(user?.settings?.theme || 'light');

  const breakpoint = getBreakpoint(screenWidth);
  const isMobile = breakpoint === 'mobile' || breakpoint === 'mobileLarge';

  // Derive theme from settings preference (not system)
  const isDark = themePreference === 'dark';
  const theme = isDark ? baseTheme.dark : baseTheme.light;

  useEffect(() => {
    if (!isWeb) return;
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isWeb && typeof document !== 'undefined') {
    document.title = 'Settings - SheetShed';
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserProfile({
        displayName: displayName.trim() || user?.displayName,
        settings: {
          defaultPageTurnMode: pageTurnMode,
          defaultBPM: parseInt(defaultBpm, 10) || 120,
          theme: themePreference,
        },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // Error handled in hook
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigation.replace('Landing');
  };

  // Subscription / billing portal
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const functions = getFunctions();
      const createPortalSession = httpsCallable(functions, 'createPortalSession');
      const origin = isWeb && typeof window !== 'undefined' ? window.location.origin : 'https://sheetshed.app';
      const result = await createPortalSession({ returnUrl: `${origin}/library` });
      const { url } = result.data;
      if (url) {
        if (isWeb && typeof window !== 'undefined') window.open(url, '_blank');
        else Linking.openURL(url);
      }
    } catch (err) {
      setPortalError(err.message || 'Unable to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const subscription = user?.subscription;
  const tier = subscription?.tier || (user?.isPro ? 'pro' : 'free');
  const isLifetime = tier === 'lifetime' || subscription?.isLifetime;
  const isPaid = tier === 'pro' || isLifetime;

  const planLabel = isLifetime ? 'Lifetime' : tier === 'pro' ? 'Pro' : 'Free';
  const planColor = isLifetime ? '#f59e0b' : tier === 'pro' ? '#8b5cf6' : null;

  const handleConnSearch = async () => {
    if (!connSearchEmail.trim()) return;
    setConnSearching(true);
    setConnSearchError('');
    setConnSearchResult(null);
    setConnSent(false);
    const results = await searchUsers(connSearchEmail.trim());
    if (results.length === 0) setConnSearchError('No user found with that email');
    else setConnSearchResult(results[0]);
    setConnSearching(false);
  };

  const handleSendConnRequest = async () => {
    if (!connSearchResult) return;
    try {
      await sendConnectionRequest(
        connSearchResult.id,
        connSearchResult.displayName || connSearchResult.email,
        connSearchResult.email,
      );
      setConnSent(true);
      setConnSearchResult(null);
      setConnSearchEmail('');
    } catch (err) {
      setConnSearchError(err.message);
    }
  };

  const getPadding = () => {
    if (breakpoint === 'mobile') return 20;
    if (breakpoint === 'mobileLarge') return 28;
    if (breakpoint === 'tablet') return 40;
    return 60;
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.surface, borderBottomColor: theme.border, paddingHorizontal: getPadding() }]}>
        <Pressable style={styles.backBtn} onPress={() => navigation.goBack()}>
          <ArrowLeftIcon color={theme.textSecondary} size={20} />
          <Text style={[styles.backText, { color: theme.textSecondary }]}>Back</Text>
        </Pressable>
        <Text style={[styles.headerTitle, { color: theme.textPrimary }]}>Settings</Text>
        <Pressable
          style={[styles.saveBtn, { backgroundColor: saved ? theme.success || '#4CAF50' : theme.primary, opacity: saving ? 0.7 : 1 }]}
          onPress={handleSave}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : saved ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <CheckIcon color="#fff" size={14} />
              <Text style={styles.saveBtnText}>Saved</Text>
            </View>
          ) : (
            <Text style={styles.saveBtnText}>Save changes</Text>
          )}
        </Pressable>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, { paddingHorizontal: getPadding() }]}
        showsVerticalScrollIndicator={false}
      >
        {/* Profile */}
        <SettingsSection icon={<UserIcon color={theme.primary} size={18} />} title="Profile" theme={theme}>
          <SettingRow
            label="Display name"
            description="How your name appears in the app"
            theme={theme}
            right={
              <TextInput
                style={[styles.inlineInput, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
                value={displayName}
                onChangeText={setDisplayName}
                placeholder="Your name"
                placeholderTextColor={theme.textMuted}
              />
            }
          />
          <SettingRow
            label="Email"
            description="Your account email"
            theme={theme}
            right={<Text style={[styles.settingValue, { color: theme.textMuted }]}>{user?.email}</Text>}
          />
          <SettingRow
            label="Plan"
            theme={theme}
            right={
              <View style={[styles.planBadge, { backgroundColor: planColor || theme.backgroundAlt }]}>
                <Text style={{ color: planColor ? '#fff' : theme.textSecondary, fontSize: 12, fontWeight: '700' }}>
                  {planLabel.toUpperCase()}
                </Text>
              </View>
            }
          />
        </SettingsSection>

        {/* Subscription management */}
        <SettingsSection icon={<CrownIcon color={theme.primary} size={18} />} title="Subscription" theme={theme}>
          <View style={[styles.planSummaryCard, { backgroundColor: isLifetime ? '#f59e0b18' : isPaid ? '#8b5cf618' : theme.backgroundAlt, borderColor: planColor || theme.border }]}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.planSummaryTitle, { color: theme.textPrimary }]}>
                {isLifetime ? 'SheetShed Lifetime' : isPaid ? 'SheetShed Pro' : 'Free Plan'}
              </Text>
              <Text style={[styles.planSummaryDesc, { color: theme.textMuted }]}>
                {isLifetime
                  ? 'One-time purchase — yours forever. Unlimited sheets, unlimited sharing.'
                  : isPaid
                  ? `Active${subscription?.currentPeriodEnd ? ` · renews ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}` : ''}`
                  : 'Up to 20 sheets · Up to 5 shares'}
              </Text>
            </View>
            {isLifetime && (
              <View style={[styles.lifetimeBadge, { backgroundColor: '#f59e0b' }]}>
                <CrownIcon color="#fff" size={14} />
              </View>
            )}
          </View>

          {portalError ? (
            <Text style={[styles.portalError, { color: theme.error || '#ef4444' }]}>{portalError}</Text>
          ) : null}

          {isPaid && !isLifetime && (
            <Pressable
              style={[styles.portalBtn, { borderColor: theme.border, opacity: portalLoading ? 0.6 : 1 }]}
              onPress={handleOpenPortal}
              disabled={portalLoading}
            >
              {portalLoading ? (
                <ActivityIndicator color={theme.primary} size="small" />
              ) : (
                <>
                  <Text style={[styles.portalBtnText, { color: theme.textPrimary }]}>Manage subscription</Text>
                  <ExternalLinkIcon color={theme.textMuted} size={15} />
                </>
              )}
            </Pressable>
          )}

          {!isPaid && (
            <Pressable
              style={[styles.upgradeBtn, { backgroundColor: theme.primary }]}
              onPress={() => navigation.navigate('Plans')}
            >
              <CrownIcon color="#fff" size={16} />
              <Text style={{ color: '#fff', fontWeight: '700', fontSize: 15, marginLeft: 8 }}>Get Lifetime Access — $14.99</Text>
            </Pressable>
          )}

          {isLifetime && (
            <View style={[styles.lifetimePerks, { borderColor: theme.border }]}>
              {['Unlimited sheet storage', 'Unlimited sharing', 'All future updates', 'Priority support'].map((perk, i) => (
                <View key={i} style={styles.perkRow}>
                  <CheckIcon color={theme.primary} size={14} />
                  <Text style={[styles.perkText, { color: theme.textSecondary }]}>{perk}</Text>
                </View>
              ))}
            </View>
          )}
        </SettingsSection>

        {/* Viewer defaults */}
        <SettingsSection icon={<MusicIcon color={theme.primary} size={18} />} title="Viewer Defaults" theme={theme}>
          <SettingRow
            label="Page turn mode"
            description="Default mode when opening a sheet"
            theme={theme}
            right={null}
          />
          <View style={styles.segmentedRow}>
            {PAGE_TURN_MODES.map(mode => (
              <Pressable
                key={mode.id}
                style={[styles.segmentBtn, {
                  backgroundColor: pageTurnMode === mode.id ? theme.primary : theme.backgroundAlt,
                  borderColor: pageTurnMode === mode.id ? theme.primary : theme.border,
                }]}
                onPress={() => setPageTurnMode(mode.id)}
              >
                <Text style={[styles.segmentBtnText, { color: pageTurnMode === mode.id ? '#fff' : theme.textSecondary }]}>
                  {mode.label}
                </Text>
              </Pressable>
            ))}
          </View>

          <SettingRow
            label="Default BPM"
            description="Starting tempo in auto page turn mode"
            theme={theme}
            right={
              <TextInput
                style={[styles.inlineInput, styles.inlineInputSmall, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
                value={defaultBpm}
                onChangeText={setDefaultBpm}
                keyboardType="numeric"
                placeholder="120"
                placeholderTextColor={theme.textMuted}
              />
            }
          />
        </SettingsSection>

        {/* Appearance */}
        <SettingsSection icon={<PaletteIcon color={theme.primary} size={18} />} title="Appearance" theme={theme}>
          <SettingRow
            label="Theme"
            description="Interface color scheme"
            theme={theme}
            right={null}
          />
          <View style={styles.segmentedRow}>
            {[{ id: 'light', label: 'Light' }, { id: 'dark', label: 'Dark' }, { id: 'system', label: 'System' }].map(t => (
              <Pressable
                key={t.id}
                style={[styles.segmentBtn, {
                  backgroundColor: themePreference === t.id ? theme.primary : theme.backgroundAlt,
                  borderColor: themePreference === t.id ? theme.primary : theme.border,
                }]}
                onPress={() => setThemePreference(t.id)}
              >
                <Text style={[styles.segmentBtnText, { color: themePreference === t.id ? '#fff' : theme.textSecondary }]}>
                  {t.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </SettingsSection>

        {/* Connections */}
        <SettingsSection icon={<UsersIcon color={theme.primary} size={18} />} title="Connections" theme={theme}>
          {/* Pending incoming requests */}
          {pendingConnections.length > 0 && (
            <View style={styles.connPendingBanner}>
              <Text style={[styles.connPendingTitle, { color: theme.primary }]}>
                {pendingConnections.length} incoming request{pendingConnections.length > 1 ? 's' : ''}
              </Text>
              {pendingConnections.map(conn => (
                <View key={conn.id} style={[styles.connRow, { borderBottomColor: theme.border }]}>
                  <View style={[styles.connAvatar, { backgroundColor: theme.primary }]}>
                    <Text style={{ color: '#fff', fontWeight: '700' }}>
                      {(conn.fromUserName || conn.fromUserEmail || '?').charAt(0).toUpperCase()}
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.connName, { color: theme.textPrimary }]}>{conn.fromUserName || '(no name)'}</Text>
                    <Text style={[styles.connEmail, { color: theme.textMuted }]}>{conn.fromUserEmail}</Text>
                  </View>
                  <Pressable style={[styles.connActionBtn, { backgroundColor: theme.primary }]} onPress={() => respondToConnection(conn.id, true)}>
                    <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Accept</Text>
                  </Pressable>
                  <Pressable style={[styles.connActionBtn, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, borderWidth: 1 }]} onPress={() => respondToConnection(conn.id, false)}>
                    <Text style={{ color: theme.textSecondary, fontSize: 13, fontWeight: '600' }}>Decline</Text>
                  </Pressable>
                </View>
              ))}
            </View>
          )}

          {/* Current connections */}
          {connections.length > 0 && (
            <View style={{ marginBottom: 16 }}>
              <Text style={[styles.connSectionLabel, { color: theme.textSecondary }]}>Your connections ({connections.length})</Text>
              {connections.map(conn => {
                const name = conn.fromUserId === user?.uid ? (conn.toUserName || conn.toUserEmail) : (conn.fromUserName || conn.fromUserEmail);
                const email = conn.fromUserId === user?.uid ? conn.toUserEmail : conn.fromUserEmail;
                return (
                  <View key={conn.id} style={[styles.connRow, { borderBottomColor: theme.border }]}>
                    <View style={[styles.connAvatar, { backgroundColor: theme.primary }]}>
                      <Text style={{ color: '#fff', fontWeight: '700' }}>{(name || '?').charAt(0).toUpperCase()}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.connName, { color: theme.textPrimary }]}>{name || '(no name)'}</Text>
                      <Text style={[styles.connEmail, { color: theme.textMuted }]}>{email}</Text>
                    </View>
                    <Pressable style={[styles.connActionBtn, { backgroundColor: theme.error + '18', borderColor: theme.error, borderWidth: 1 }]} onPress={() => removeConnection(conn.id)}>
                      <Text style={{ color: theme.error, fontSize: 13, fontWeight: '600' }}>Remove</Text>
                    </Pressable>
                  </View>
                );
              })}
            </View>
          )}

          {/* Sent pending requests */}
          {sentRequests.length > 0 && (
            <View style={{ marginBottom: 16 }}>
              <Text style={[styles.connSectionLabel, { color: theme.textSecondary }]}>Sent requests ({sentRequests.length})</Text>
              {sentRequests.map(conn => (
                <View key={conn.id} style={[styles.connRow, { borderBottomColor: theme.border }]}>
                  <View style={[styles.connAvatar, { backgroundColor: theme.backgroundAlt }]}>
                    <Text style={{ color: theme.textMuted, fontWeight: '700' }}>{(conn.toUserName || conn.toUserEmail || '?').charAt(0).toUpperCase()}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.connName, { color: theme.textPrimary }]}>{conn.toUserName || '(no name)'}</Text>
                    <Text style={[styles.connEmail, { color: theme.textMuted }]}>{conn.toUserEmail} · Pending</Text>
                  </View>
                  <Pressable style={[styles.connActionBtn, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, borderWidth: 1 }]} onPress={() => removeConnection(conn.id)}>
                    <Text style={{ color: theme.textSecondary, fontSize: 13, fontWeight: '600' }}>Cancel</Text>
                  </Pressable>
                </View>
              ))}
            </View>
          )}

          {/* Search to connect */}
          <Text style={[styles.connSectionLabel, { color: theme.textSecondary, marginBottom: 10 }]}>Connect with someone</Text>
          <View style={[styles.connSearchRow, { backgroundColor: theme.backgroundAlt, borderColor: theme.border }]}>
            <TextInput
              style={[styles.connSearchInput, { color: theme.textPrimary }]}
              value={connSearchEmail}
              onChangeText={(t) => { setConnSearchEmail(t); setConnSearchError(''); setConnSearchResult(null); setConnSent(false); }}
              placeholder="Search by email..."
              placeholderTextColor={theme.textMuted}
              keyboardType="email-address"
              autoCapitalize="none"
              onSubmitEditing={handleConnSearch}
            />
            <Pressable style={[styles.connSearchBtn, { backgroundColor: theme.primary, opacity: connSearching ? 0.7 : 1 }]} onPress={handleConnSearch} disabled={connSearching}>
              {connSearching ? <ActivityIndicator size="small" color="#fff" /> : <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>Find</Text>}
            </Pressable>
          </View>
          {connSearchError ? <Text style={[{ color: theme.error, fontSize: 13, marginTop: 6 }]}>{connSearchError}</Text> : null}
          {connSent && <Text style={[{ color: '#22C55E', fontSize: 13, marginTop: 6, fontWeight: '600' }]}>Connection request sent!</Text>}
          {connSearchResult && !connSent && (
            <View style={[styles.connRow, { borderBottomColor: theme.border, marginTop: 10 }]}>
              <View style={[styles.connAvatar, { backgroundColor: theme.primary }]}>
                <Text style={{ color: '#fff', fontWeight: '700' }}>{(connSearchResult.displayName || connSearchResult.email || '?').charAt(0).toUpperCase()}</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[styles.connName, { color: theme.textPrimary }]}>{connSearchResult.displayName || '(no name)'}</Text>
                <Text style={[styles.connEmail, { color: theme.textMuted }]}>{connSearchResult.email}</Text>
              </View>
              <Pressable style={[styles.connActionBtn, { backgroundColor: theme.primary }]} onPress={handleSendConnRequest}>
                <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Connect</Text>
              </Pressable>
            </View>
          )}

          {connections.length === 0 && pendingConnections.length === 0 && sentRequests.length === 0 && (
            <Text style={[styles.connEmpty, { color: theme.textMuted }]}>No connections yet. Search for a user above to connect.</Text>
          )}
        </SettingsSection>

        {/* Account */}
        <SettingsSection icon={<ShieldIcon color={theme.primary} size={18} />} title="Account" theme={theme}>
          <SettingRow
            label="Member since"
            theme={theme}
            right={
              <Text style={[styles.settingValue, { color: theme.textMuted }]}>
                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '—'}
              </Text>
            }
          />
          <Pressable
            style={[styles.signOutBtn, { backgroundColor: theme.backgroundAlt, borderColor: theme.border }]}
            onPress={handleSignOut}
          >
            <Text style={[styles.signOutText, { color: theme.textPrimary }]}>Sign out</Text>
          </Pressable>
          <View style={[styles.dangerZone, { borderColor: theme.error + '40' }]}>
            <Text style={[styles.dangerTitle, { color: theme.error }]}>Danger Zone</Text>
            <Text style={[styles.dangerDesc, { color: theme.textMuted }]}>Deleting your account permanently removes all your sheets and data. This cannot be undone.</Text>
            <Pressable
              style={[styles.deleteBtn, { borderColor: theme.error }]}
              onPress={() => {
                if (isWeb && window.confirm('Are you sure? This will permanently delete your account and all data.')) {
                  // TODO: implement account deletion
                }
              }}
            >
              <Text style={[styles.deleteBtnText, { color: theme.error }]}>Delete account</Text>
            </Pressable>
          </View>
        </SettingsSection>

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollView: { flex: 1 },
  scrollContent: { paddingTop: 24, paddingBottom: 60 },

  // Header
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 16, borderBottomWidth: 1,
    ...Platform.select({ web: { position: 'sticky', top: 0, zIndex: 100 } }),
  },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 4 },
  backText: { fontSize: 15, fontWeight: '500' },
  headerTitle: { fontSize: 18, fontWeight: '700' },
  saveBtn: { paddingHorizontal: 20, paddingVertical: 10, borderRadius: 999, flexDirection: 'row', alignItems: 'center', gap: 6 },
  saveBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },

  // Section
  section: { borderRadius: 20, marginBottom: 20, overflow: 'hidden', ...Platform.select({ web: { boxShadow: '0 2px 12px rgba(0,0,0,0.05)' } }) },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 20, paddingBottom: 12 },
  sectionIconWrap: { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  sectionTitle: { fontSize: 16, fontWeight: '700' },
  sectionBody: { paddingHorizontal: 20, paddingBottom: 16 },

  // Setting row
  settingRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 14, borderBottomWidth: 1, gap: 16 },
  settingLabel: { fontSize: 15, fontWeight: '500', marginBottom: 2 },
  settingDesc: { fontSize: 13 },
  settingValue: { fontSize: 14 },

  // Inputs
  inlineInput: {
    borderRadius: 10, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 8,
    fontSize: 14, minWidth: 140,
    ...Platform.select({ web: { outlineStyle: 'none' } }),
  },
  inlineInputSmall: { minWidth: 70, textAlign: 'center' },

  // Segmented control
  segmentedRow: { flexDirection: 'row', gap: 8, paddingTop: 4, paddingBottom: 12, flexWrap: 'wrap' },
  segmentBtn: { paddingHorizontal: 16, paddingVertical: 9, borderRadius: 999, borderWidth: 1.5 },
  segmentBtnText: { fontSize: 14, fontWeight: '600' },

  // Plan / upgrade
  planBadge: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 999 },
  upgradeBtn: { marginTop: 4, marginBottom: 8, paddingVertical: 14, borderRadius: 999, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },

  // Subscription section
  planSummaryCard: { borderRadius: 16, borderWidth: 1.5, padding: 16, flexDirection: 'row', alignItems: 'center', marginBottom: 14 },
  planSummaryTitle: { fontSize: 16, fontWeight: '700', marginBottom: 3 },
  planSummaryDesc: { fontSize: 13, lineHeight: 18 },
  lifetimeBadge: { width: 34, height: 34, borderRadius: 17, justifyContent: 'center', alignItems: 'center', marginLeft: 12 },
  portalBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderWidth: 1.5, borderRadius: 999, paddingVertical: 13, marginBottom: 10 },
  portalBtnText: { fontSize: 15, fontWeight: '600' },
  portalError: { fontSize: 13, marginBottom: 10 },
  lifetimePerks: { borderTopWidth: 1, marginTop: 4, paddingTop: 14 },
  perkRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 },
  perkText: { fontSize: 14 },

  // Sign out
  signOutBtn: { borderWidth: 1, borderRadius: 999, paddingVertical: 14, alignItems: 'center', marginTop: 8, marginBottom: 16 },
  signOutText: { fontSize: 15, fontWeight: '600' },

  // Danger zone
  dangerZone: { borderWidth: 1.5, borderRadius: 16, padding: 16, borderStyle: 'dashed' },
  dangerTitle: { fontSize: 14, fontWeight: '700', marginBottom: 6 },
  dangerDesc: { fontSize: 13, lineHeight: 20, marginBottom: 12 },
  deleteBtn: { borderWidth: 1.5, borderRadius: 999, paddingVertical: 12, alignItems: 'center' },
  deleteBtnText: { fontSize: 14, fontWeight: '600' },

  // Connections
  connPendingBanner: { marginBottom: 16 },
  connPendingTitle: { fontSize: 14, fontWeight: '700', marginBottom: 10 },
  connSectionLabel: { fontSize: 13, fontWeight: '600', marginBottom: 8 },
  connRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, gap: 10 },
  connAvatar: { width: 38, height: 38, borderRadius: 19, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  connName: { fontSize: 15, fontWeight: '600' },
  connEmail: { fontSize: 13, marginTop: 1 },
  connActionBtn: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999 },
  connSearchRow: { flexDirection: 'row', alignItems: 'center', borderRadius: 14, borderWidth: 1, overflow: 'hidden' },
  connSearchInput: { flex: 1, padding: 12, fontSize: 14, ...Platform.select({ web: { outlineStyle: 'none' } }) },
  connSearchBtn: { paddingHorizontal: 16, paddingVertical: 12 },
  connEmpty: { fontSize: 14, textAlign: 'center', paddingVertical: 16 },
});

export default SettingsScreen;
